// (imports remain unchanged)
import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Segment,
  Header,
  Icon,
  Message,
  List,
  Label,
  Divider,
  Placeholder,
  Pagination,
  Dropdown,
  Grid,
} from "semantic-ui-react";
import { loanApi } from "@api/loans";

const NotificationItem = ({
  type,
  title,
  message,
  date,
  severity = "info",
}) => {
  const getIconName = () => {
    switch (type) {
      case "overdue":
        return "clock outline";
      case "fine":
        return "dollar sign";
      case "borrow":
        return "book";
      case "material_request":
        return "file alternate outline";
      case "system":
        return "cog";
      case "approval":
        return "check circle";
      default:
        return "bell outline";
    }
  };

  const getColor = () => {
    switch (severity) {
      case "error":
        return "red";
      case "warning":
        return "orange";
      case "return":
        return "yellow";
      case "success":
        return "green";
      default:
        return "blue";
    }
  };

  return (
    <List.Item>
      <List.Content>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "0.5rem",
          }}
        >
          <Icon name={getIconName()} color={getColor()} />
          <List.Header style={{ marginLeft: "0.5rem" }}>{title}</List.Header>
          <Label size="mini" color="green" style={{ marginLeft: "auto" }}>
            {date}
          </Label>
        </div>
        <List.Description style={{ marginLeft: "1.5rem" }}>
          {message}
        </List.Description>
      </List.Content>
    </List.Item>
  );
};

NotificationItem.propTypes = {
  type: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  severity: PropTypes.oneOf(["info", "warning", "error", "success", "return"]),
};

const NotificationSection = ({
  title,
  icon,
  notifications,
  emptyMessage,
  itemsPerPage = 5,
  showPagination = true,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(itemsPerPage);

  const totalPages = Math.ceil(notifications.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentNotifications = notifications.slice(startIndex, endIndex);

  const pageSizeOptions = [
    { key: 5, text: "5 per page", value: 5 },
    { key: 10, text: "10 per page", value: 10 },
    { key: 20, text: "20 per page", value: 20 },
    { key: 50, text: "50 per page", value: 50 },
  ];

  const handlePageChange = (e, { activePage }) => {
    setCurrentPage(activePage);
  };

  const handlePageSizeChange = (e, { value }) => {
    setPageSize(value);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [notifications.length, totalPages, currentPage]);

  return (
    <Segment>
      <Header as="h3">
        <Icon name={icon} />
        <Header.Content>
          {title}
          {notifications.length > 0 && (
            <Label size="small" color="grey" style={{ marginLeft: "0.5rem" }}>
              {notifications.length} total
            </Label>
          )}
        </Header.Content>
      </Header>

      {notifications.length > 0 ? (
        <>
          <List divided relaxed>
            {currentNotifications.map((notification, index) => (
              <NotificationItem key={startIndex + index} {...notification} />
            ))}
          </List>

          {showPagination && totalPages > 1 && (
            <div style={{ marginTop: "1rem" }}>
              <Grid>
                <Grid.Column width={16} textAlign="center">
                  <Pagination
                    activePage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    size="mini"
                    pointing
                    secondary
                  />
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      marginLeft: "1rem",
                    }}
                  >
                    <span
                      style={{
                        marginRight: "0.5rem",
                        fontSize: "0.9em",
                        color: "#666",
                      }}
                    >
                      Showing {startIndex + 1}-
                      {Math.min(endIndex, notifications.length)} of{" "}
                      {notifications.length}
                    </span>
                    <Dropdown
                      selection
                      compact
                      options={pageSizeOptions}
                      value={pageSize}
                      onChange={handlePageSizeChange}
                      style={{ minWidth: "120px" }}
                    />
                  </div>
                </Grid.Column>
              </Grid>
            </div>
          )}
        </>
      ) : (
        <Message info>
          <Message.Content>{emptyMessage}</Message.Content>
        </Message>
      )}
    </Segment>
  );
};

NotificationSection.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  notifications: PropTypes.array.isRequired,
  emptyMessage: PropTypes.string.isRequired,
  itemsPerPage: PropTypes.number,
  showPagination: PropTypes.bool,
};

const LoadingPlaceholder = () => (
  <Segment>
    <Placeholder>
      <Placeholder.Header>
        <Placeholder.Line />
      </Placeholder.Header>
      <Placeholder.Paragraph>
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
      </Placeholder.Paragraph>
    </Placeholder>
  </Segment>
);

export const PatronNotifications = ({ patronPid, userRole }) => {
  const [notifications, setNotifications] = useState({
    overdue: [],
    fines: [],
    borrow: [],
    materialRequests: [],
    systemNotifications: [],
    approvalRequests: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isLibrarian = useMemo(() => {
    const roles = [
      "librarian",
      "admin",
      "staff",
      "library_staff",
      "administrator",
      "lib_admin",
    ];
    const role = (userRole || "").toLowerCase();
    return roles.includes(role);
  }, [userRole]);

  const isFaculty = useMemo(() => {
    const roles = ["faculty", "teacher", "professor", "instructor"];
    const role = (userRole || "").toLowerCase();
    return roles.includes(role);
  }, [userRole]);

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? dateString : date.toLocaleDateString();
    } catch {
      return dateString || "Unknown date";
    }
  };

  const formatLoanNotification = (loan, type) => {
    const state = loan.state || loan.metadata?.state;
    const document = loan.document || loan.metadata?.document;
    const item = loan.item || loan.metadata?.item;

    const baseNotification = {
      type,
      date: formatDate(
        loan.created || loan.start_date || loan._updated || new Date()
      ),
    };

    switch (type) {
      case "overdue":
        return {
          ...baseNotification,
          title: "Overdue Item",
          message: `"${
            document?.title || item?.barcode || "Item"
          }" is overdue since ${formatDate(loan.metadata?.end_date)}`,
          severity: "error",
        };
      case "borrow": {
        const statusMap = {
          ITEM_ON_LOAN: "checked out",
          PENDING: "pending approval",
          ITEM_RETURNED: "checked in",
          CANCELLED: "cancelled",
        };
        return {
          ...baseNotification,
          title: `Item ${statusMap[state] || state}`,
          message: `"${document?.title || item?.barcode || "Item"}" has been ${
            statusMap[state] || state
          }`,
          severity:
            state === "ITEM_ON_LOAN"
              ? "success"
              : state === "ITEM_RETURNED"
              ? "return"
              : state === "CANCELLED"
              ? "error"
              : "info",
        };
      }
      default:
        return baseNotification;
    }
  };

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const fetchNotifications = async () => {
      if (!isMounted) return;

      try {
        setLoading(true);
        setError(null);

        const overdueQuery = isLibrarian
          ? loanApi
              .query()
              .withState("ITEM_ON_LOAN")
              .overdue()
              .withSize(100)
              .sortByNewest()
              .qs()
          : loanApi
              .query()
              .withPatronPid(patronPid)
              .withState("ITEM_ON_LOAN")
              .overdue()
              .withSize(50)
              .sortByNewest()
              .qs();

        const recentLoansQuery = isLibrarian
          ? loanApi.query().withSize(100).sortByNewest().qs()
          : loanApi
              .query()
              .withPatronPid(patronPid)
              .withSize(50)
              .sortByNewest()
              .qs();

        let overdueResponse = { data: { hits: [] } };
        let recentLoansResponse = { data: { hits: [] } };

        overdueResponse = await loanApi.list(overdueQuery, {
          signal: abortController.signal,
        });

        if (!isMounted) return;

        recentLoansResponse = await loanApi.list(recentLoansQuery, {
          signal: abortController.signal,
        });

        if (!isMounted) return;

        const overdueNotifications = (overdueResponse.data.hits || []).map(
          (loan) => formatLoanNotification(loan, "overdue")
        );

        const fineNotifications = (recentLoansResponse.data.hits || [])
          .filter((loan) => {
            const endDate = new Date(loan.end_date || loan.metadata?.end_date);
            const now = new Date();
            const tomorrow = new Date(now);
            tomorrow.setDate(now.getDate() + 1);
            return (
              endDate.getFullYear() === tomorrow.getFullYear() &&
              endDate.getMonth() === tomorrow.getMonth() &&
              endDate.getDate() === tomorrow.getDate()
            );
          })
          .map((loan) => {
            const document = loan.document || loan.metadata?.document;
            const item = loan.item || loan.metadata?.item;
            return {
              type: "fine",
              title: "Due Tomorrow",
              message: `"${
                document?.title || item?.barcode || "Item"
              }" is due tomorrow (${formatDate(
                loan.metadata?.end_date
              )}). Please return or renew to avoid fines.`,
              date: formatDate(new Date()),
              severity: "warning",
            };
          });

        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

        const recentBorrowNotifications = (recentLoansResponse.data.hits || [])
          .filter((loan) => {
            const date = new Date(
              loan.created || loan.start_date || loan._updated
            );
            return date >= sevenDaysAgo;
          })
          .map((loan) => formatLoanNotification(loan, "borrow"));

        const approvalRequests = isLibrarian
          ? recentBorrowNotifications.filter((n) =>
              n.title.toLowerCase().includes("pending approval")
            )
          : [];

        const overduePendingApprovals = isLibrarian
          ? (recentLoansResponse.data.hits || [])
              .filter((loan) => {
                const state = loan.state || loan.metadata?.state;
                const endDate = new Date(
                  loan.end_date || loan.metadata?.end_date
                );
                return (
                  state === "PENDING" &&
                  endDate instanceof Date &&
                  !isNaN(endDate) &&
                  endDate < new Date()
                );
              })
              .map((loan) => ({
                ...formatLoanNotification(loan, "overdue"),
                title: "Pending Approval Overdue",
              }))
          : [];

        const systemNotifications = isLibrarian
          ? [
              {
                type: "system",
                title: "System Status",
                message: "Library management system is running normally",
                date: formatDate(new Date()),
                severity: "success",
              },
            ]
          : [];

        const finalNotifications = {
          overdue: [...overdueNotifications, ...overduePendingApprovals],
          fines: fineNotifications,
          borrow: recentBorrowNotifications,
          materialRequests: [],
          systemNotifications,
          approvalRequests,
        };

        if (isMounted) {
          setNotifications(finalNotifications);
        }
      } catch (err) {
        if (isMounted && err.name !== "AbortError") {
          setError(
            `Failed to load notifications: ${err.message || "Unknown error"}`
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (patronPid || isLibrarian) {
      fetchNotifications();
    } else {
      if (isMounted) {
        setLoading(false);
      }
    }

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [patronPid, userRole, isLibrarian, isFaculty]);

  if (loading)
    return (
      <>
        <LoadingPlaceholder />
        <LoadingPlaceholder />
        <LoadingPlaceholder />
      </>
    );

  if (error) {
    return (
      <Message error>
        <Message.Header>Error Loading Notifications</Message.Header>
        <p>{error}</p>
      </Message>
    );
  }

  return (
    <div>
      <Header as="h2">
        <Icon name="bell outline" />
        <Header.Content>
          Notifications
          {isLibrarian && (
            <Label color="blue" size="mini" style={{ marginLeft: "0.5rem" }}>
              Librarian View
            </Label>
          )}
          {isFaculty && (
            <Label color="green" size="mini" style={{ marginLeft: "0.5rem" }}>
              Faculty View
            </Label>
          )}
          <Header.Subheader style={{ color: "gray", fontSize: "13px" }}>
            Stay updated with your library activity
          </Header.Subheader>
        </Header.Content>
      </Header>

      <NotificationSection
        title="Overdue Notices"
        icon="clock outline"
        notifications={notifications.overdue}
        emptyMessage="No overdue items. Great job!"
        itemsPerPage={5}
      />

      <NotificationSection
        title="Fine Alerts"
        icon="dollar sign"
        notifications={notifications.fines}
        emptyMessage="No items are due tomorrow."
        itemsPerPage={5}
      />

      <NotificationSection
        title="Borrow Status Updates"
        icon="book"
        notifications={notifications.borrow}
        emptyMessage="No recent borrow status updates."
        itemsPerPage={10}
      />

      {(isFaculty || isLibrarian) && (
        <>
          <Divider />
          <Header as="h3" color="teal">
            <Icon name="graduation cap" />
            {isFaculty ? "Faculty" : "Librarian"} Notifications
          </Header>

          <NotificationSection
            title="Pending Approval Requests"
            icon="check circle"
            notifications={notifications.approvalRequests}
            emptyMessage="No pending approval requests."
            itemsPerPage={10}
          />
        </>
      )}
    </div>
  );
};

PatronNotifications.propTypes = {
  patronPid: PropTypes.string.isRequired,
  userRole: PropTypes.string.isRequired,
};
